import { ApiError } from '@lib/api';
import {
    Badge,
    Button,
    Card,
    CardHeader,
    ErrorState,
    LoadingRows,
    PageHeader,
    TextField,
} from '@shared/ui';
import { useState } from 'react';
import { Link, useParams } from 'react-router';

import { useClientTarget } from '../hooks/use-client-target';
import { useClient, useProvisionOwner } from '../hooks/use-clients';
import { describeTarget, targetParams } from '../schemas/target';

import { emailError, usernameError } from './field-errors';

import type { ProvisionedOwner } from '../schemas/client';

/**
 * Finish a client whose first person never landed.
 *
 * ## Why this is not a "retry"
 *
 * Sending the creation again would fail on the address this very client already
 * holds. Finishing it is a different operation on a different input — the
 * tenant id — and it is the only way out of the state a failed creation leaves.
 *
 * ## The handle usually cannot be reused, and that has to be said
 *
 * Upstream writes the person into its access store BEFORE creating the Cognito
 * credential, so a failure in the second half leaves the handle held by the
 * half-written person. Resuming with the same one is refused with a conflict
 * about a name that is, in effect, its own.
 *
 * Rather than hide that behind a generic error, the screen says it and asks for
 * a different handle. Anything else leaves an operator retrying the same value
 * forever against an error that reads like their mistake.
 */
export function ProvisionOwnerPage() {
    const { tenantId = '' } = useParams();
    const targetState = useClientTarget();
    const { target } = targetState;

    const client = useClient(target, tenantId);
    const provision = useProvisionOwner(target, tenantId);

    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [done, setDone] = useState<ProvisionedOwner | null>(null);

    const search = new URLSearchParams(targetParams(target)).toString();
    const conflict =
        provision.error instanceof ApiError && provision.error.code === 'OWNER_CONFLICT';

    if (client.isPending) return <LoadingRows label="Loading the client…" />;
    if (client.isError) {
        return <ErrorState error={client.error} onRetry={() => void client.refetch()} />;
    }
    if (!client.data) {
        return (
            <ErrorState error={new Error(`No client with that id in ${describeTarget(target)}.`)} />
        );
    }

    if (done) {
        return (
            <>
                <PageHeader
                    eyebrow="Tenants · Onboarding"
                    title="Owner added"
                    description={`${client.data.displayName} now has a person who can sign in.`}
                />
                <Card>
                    <CardHeader
                        title="Sign-in credentials"
                        action={
                            <Badge tone="warning" dot>
                                Shown once
                            </Badge>
                        }
                    />
                    <div className="flex flex-col gap-3 p-5">
                        <Field label="Username" value={done.username} />
                        {done.temporaryPassword ? (
                            <Field label="Temporary password" value={done.temporaryPassword} />
                        ) : (
                            <p className="rounded-lg border border-info-line bg-info-surface px-3.5 py-3 text-[12.5px] text-info-text">
                                No new password was minted — the credential already existed and its
                                password was handed out on the attempt that created it.
                            </p>
                        )}
                        <p className="rounded-lg border border-warning-line bg-warning-surface px-3.5 py-3 text-[12.5px] text-warning-text">
                            Leaving this screen removes the password from the Backoffice. There is
                            no recovery and no resend.
                        </p>
                    </div>
                    <div className="flex justify-end border-t border-line-subtle bg-surface-sunken px-5 py-3.5">
                        <Link to={{ pathname: '/clients', search }}>
                            <Button>Done</Button>
                        </Link>
                    </div>
                </Card>
            </>
        );
    }

    const complete =
        username.trim().length > 0 &&
        usernameError(username.trim()) === undefined &&
        email.trim().length > 0 &&
        emailError(email.trim()) === undefined;

    return (
        <>
            <PageHeader
                eyebrow="Tenants · Onboarding"
                title="Add the owner"
                description={`${client.data.displayName} was created without a first person. Give it one.`}
                actions={
                    <Link to={{ pathname: '/clients', search }}>
                        <Button variant="ghost">Cancel</Button>
                    </Link>
                }
            />

            <Card>
                <CardHeader title="The owner" subtitle={describeTarget(target)} />
                <div className="flex flex-col gap-5 p-5">
                    {conflict && (
                        <div className="rounded-lg border border-warning-line bg-warning-surface px-3.5 py-3">
                            <p className="text-[13px] font-semibold text-warning-strong">
                                That handle is not available — most likely because of the attempt
                                that failed.
                            </p>
                            <p className="mt-1 text-[12.5px] leading-relaxed text-warning-text">
                                {provision.error?.message} A failed provisioning registers the
                                person before creating their credential, so the handle it used stays
                                held. Choose a different one.
                            </p>
                        </div>
                    )}

                    {provision.isError && !conflict && (
                        <div className="rounded-lg border border-danger-line bg-danger-surface px-3.5 py-3 text-[12.5px] text-danger-text">
                            {provision.error.message}
                        </div>
                    )}

                    <TextField
                        label="Username"
                        value={username}
                        onChange={setUsername}
                        placeholder="a.gomez"
                        mono
                        autoFocus
                        error={usernameError(username.trim())}
                        hint="What they sign in with. Handles are unique across every client."
                    />

                    <TextField
                        label="Email"
                        value={email}
                        onChange={setEmail}
                        placeholder="admin@example.org"
                        error={emailError(email.trim())}
                        hint="Contact only. Not what they sign in with."
                    />

                    <p className="rounded-lg border border-warning-line bg-warning-surface px-3.5 py-3 text-[12.5px] leading-relaxed text-warning-text">
                        <b>No email is sent.</b> The password appears on the next screen, once.
                    </p>
                </div>
                <div className="flex items-center justify-end border-t border-line-subtle bg-surface-sunken px-5 py-3.5">
                    <Button
                        disabled={!complete || provision.isPending}
                        onClick={() => {
                            provision.mutate(
                                { username: username.trim(), email: email.trim() },
                                { onSuccess: setDone },
                            );
                        }}
                    >
                        {provision.isPending ? 'Adding…' : 'Add owner'}
                    </Button>
                </div>
            </Card>
        </>
    );
}

function Field({ label, value }: { label: string; value: string }) {
    const [copied, setCopied] = useState(false);

    return (
        <div className="flex items-center gap-3 rounded-lg border border-line bg-surface-sunken px-3.5 py-3">
            <span className="w-36 shrink-0 text-[12px] text-content-muted">{label}</span>
            <span className="flex-1 break-all font-mono text-[13.5px]">{value}</span>
            <button
                type="button"
                onClick={() => {
                    void navigator.clipboard.writeText(value).finally(() => {
                        setCopied(true);
                        setTimeout(() => {
                            setCopied(false);
                        }, 1600);
                    });
                }}
                className="shrink-0 rounded-md border border-line bg-surface px-2.5 py-1 text-[12px] font-semibold text-content-body hover:bg-surface-sunken"
            >
                {copied ? 'Copied' : 'Copy'}
            </button>
        </div>
    );
}
